import dayjs from "dayjs";
import { API_OCCUPANCY_REPORT_EXPORT } from "../../../../utils/apis";
import {
  beautify,
  NoWrap,
  numberFormat,
  returnObject,
} from "../../../../utils/helpers";

type TOccupancyReportProps = {
  dateFilterKey?: string;
  status?: string;
  noWrapColumns?: string[];
  numberColumns?: string[];
};

const useOccupancyReport = ({
  dateFilterKey,
  noWrapColumns = [],
  numberColumns = [],
}: TOccupancyReportProps) => {
  const tableColumns = (data: any) => {
    const firstItem = data[0] || {};

    // Custom columns for occupancy data with specific filters
    const filterColumns = [
      {
        id: "slot_number",
        header: <NoWrap>Slot Number</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "occupancy_status",
        header: <NoWrap>Occupancy Status</NoWrap>,
        filter: {
          type: "select",
          options: [
            { value: "Available", label: "Available" },
            { value: "Occupied", label: "Occupied" },
            { value: "Reserved", label: "Reserved" },
            { value: "Out of Order", label: "Out of Order" },
          ],
        },
      },
      {
        id: "street_name",
        header: <NoWrap>Street Name</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "parking_yard",
        header: <NoWrap>Parking Yard</NoWrap>,
        filter: { type: "text" },
      },
    ];

    // Generate columns from data structure
    const columns = Object.keys(firstItem).map((column: any) => ({
      id: column,
      header: <NoWrap>{beautify(column)}</NoWrap>,
      filter: [
        "occupancy_status",
        "parking_yard",
        "street_name",
        "slot_number",
      ].includes(column)
        ? false
        : true,
    }));

    return [...filterColumns, ...columns];
  };

  const tableData = (data: any) => {
    return data?.data?.map((item: any) => ({
      ...item,
      [dateFilterKey || "created_at"]: (
        <NoWrap>{item[dateFilterKey || "created_at"]}</NoWrap>
      ),
      slot_number: <NoWrap>{item.slot_number}</NoWrap>,
      occupancy_status: (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            item.occupancy_status === "Available"
              ? "bg-green-100 text-green-800"
              : item.occupancy_status === "Occupied"
                ? "bg-red-100 text-red-800"
                : item.occupancy_status === "Reserved"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-800"
          }`}
        >
          {item.occupancy_status}
        </span>
      ),
      parking_yard: <NoWrap>{item.parking_yard}</NoWrap>,
      street_name: <NoWrap>{item.street_name}</NoWrap>,
      ...returnObject(
        noWrapColumns.length > 0,
        noWrapColumns.reduce((obj, column) => {
          if (item[column] !== undefined) {
            obj[column] = <NoWrap>{item[column]}</NoWrap>;
          }
          return obj;
        }, {}),
      ),
      ...returnObject(
        numberColumns.length > 0,
        numberColumns.reduce((obj, column) => {
          if (item[column] !== undefined) {
            obj[column] = numberFormat(item[column]);
          }
          return obj;
        }, {}),
      ),
    }));
  };

  const initialFilters = {
    start_date: dayjs().format("YYYY-MM-DD"),
    end_date: dayjs().format("YYYY-MM-DD"),
  };

  // Additional utility functions for occupancy analysis
  const getOccupancyStats = (data: any) => {
    const total = data.length;
    const available = data.filter(
      (item: any) => item.occupancy_status === "Available",
    ).length;
    const occupied = data.filter(
      (item: any) => item.occupancy_status === "Occupied",
    ).length;
    const reserved = data.filter(
      (item: any) => item.occupancy_status === "Reserved",
    ).length;
    const outOfOrder = data.filter(
      (item: any) => item.occupancy_status === "Out of Order",
    ).length;

    return {
      total,
      available,
      occupied,
      reserved,
      outOfOrder,
      occupancyRate:
        total > 0 ? (((occupied + reserved) / total) * 100).toFixed(1) : 0,
      availabilityRate: total > 0 ? ((available / total) * 100).toFixed(1) : 0,
    };
  };

  const getYardSummary = (data: any) => {
    const yardGroups = data.reduce((acc: any, item: any) => {
      const yard = item.parking_yard;
      if (!acc[yard]) {
        acc[yard] = {
          total: 0,
          available: 0,
          occupied: 0,
          reserved: 0,
          outOfOrder: 0,
        };
      }
      acc[yard].total++;
      acc[yard][item.occupancy_status.toLowerCase().replace(" ", "_")] =
        (acc[yard][item.occupancy_status.toLowerCase().replace(" ", "_")] ||
          0) + 1;
      return acc;
    }, {});

    return Object.entries(yardGroups).map(([yard, stats]: [string, any]) => ({
      parking_yard: yard,
      ...stats,
      occupancyRate: (
        ((stats.occupied + stats.reserved) / stats.total) *
        100
      ).toFixed(1),
    }));
  };

  const exportData = {
    api: API_OCCUPANCY_REPORT_EXPORT,
    fileName: `parking_occupancy_report${new Date().toISOString().split("T")[0]}.xlsx`,
    showCustomDateRange: true,
    start_date: "start_date",
    end_date: "end_date",
  };

  const externalFilters = [
    {
      id: "payment_date",
      label: "Payment Date Range",
      type: "date",
      options: [],
      dateRangeNames: {
        startDateKey: "start_date",
        endDateKey: "end_date",
      },
    },
  ];

  return {
    tableColumns,
    tableData,
    initialFilters,
    externalFilters,
    exportData,
    getOccupancyStats,
    getYardSummary,
  };
};

export default useOccupancyReport;

// Specific implementation for parking occupancy
export const useParkingOccupancyReport = () => {
  const noWrapColumns = ["slot_number", "parking_yard", "street_name"];
  const numberColumns: string[] = [];
  // Add any numeric columns if needed

  const {
    tableColumns,
    tableData,
    initialFilters,
    getOccupancyStats,
    getYardSummary,
  } = useOccupancyReport({
    dateFilterKey: "last_updated",
    numberColumns,
    noWrapColumns,
  });

  return {
    tableColumns,
    tableData,
    initialFilters,
    getOccupancyStats,
    getYardSummary,
  };
};

// Usage example with summary component
export const useParkingDashboard = () => {
  const {
    tableColumns,
    tableData,
    initialFilters,
    getOccupancyStats,
    getYardSummary,
  } = useParkingOccupancyReport();

  const renderOccupancySummary = (data: any) => {
    const stats = getOccupancyStats(data?.data);

    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-blue-600">Total Slots</h3>
          <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-green-600">Available</h3>
          <p className="text-2xl font-bold text-green-900">{stats.available}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-red-600">Occupied</h3>
          <p className="text-2xl font-bold text-red-900">{stats.occupied}</p>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-yellow-600">Reserved</h3>
          <p className="text-2xl font-bold text-yellow-900">{stats.reserved}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-purple-600">
            Occupancy Rate
          </h3>
          <p className="text-2xl font-bold text-purple-900">
            {stats.occupancyRate}%
          </p>
        </div>
      </div>
    );
  };

  return {
    tableColumns,
    tableData,
    initialFilters,
    getOccupancyStats,
    getYardSummary,
    renderOccupancySummary,
  };
};
